import { useMemo, useState } from 'react';
import { ChefHat, ShoppingBasket, PackageCheck } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@beatq/ui';
import { PermissionGate, usePermissionState } from '@beatq/shared';
import { MerchantScreenFrame as ScreenFrame } from '../../shell/MerchantScreenFrame.js';

import { getKitchenBoard, ordersFromEnvelope } from '../kitchen-display/kitchenDisplayApi.js';
import { pickListPath, setStoredOrderId } from '../kitchen-display/kitchenDisplayState.js';
import { orderBoardPath } from '../order-board/orderBoardState.js';
import { markFloorSession, type FloorMode } from './floorModeState.js';

export type { FloorMode };
export { floorModeSwitcherPath, markFloorSession, isFloorSessionActive } from './floorModeState.js';

export function FloorModeSwitcherPage() {
  const navigate = useNavigate();
  const kitchenPerm = usePermissionState('PERM-fulfilment.preparation.start');
  const pickPerm = usePermissionState('PERM-fulfilment.pick.execute');
  const counterPerm = usePermissionState('PERM-fulfilment.handover.complete');

  const boardQuery = useQuery({
    queryKey: ['merchant', 'kitchen', 'board', 'floor-picker'],
    queryFn: () => getKitchenBoard(),
  });
  const pickOrderId = useMemo(() => {
    const orders = ordersFromEnvelope(boardQuery.data);
    const preferred = orders.find(
      (order) => order.status === 'PREPARING' || order.status === 'ACCEPTED',
    );
    return preferred?.id ?? null;
  }, [boardQuery.data]);

  const modes: {
    id: FloorMode;
    title: string;
    blurb: string;
    enabled: boolean;
    go: () => void;
  }[] = [
    {
      id: 'kitchen',
      title: 'Kitchen',
      blurb: 'Ticket grid, start prep, delay, bump',
      enabled: kitchenPerm !== 'hidden',
      go: () => {
        markFloorSession(true);
        navigate('/kitchen-display');
      },
    },
    {
      id: 'picker',
      title: 'Picker',
      blurb: boardQuery.isError
        ? 'Orders unavailable — return to live orders and retry'
        : boardQuery.isLoading
          ? 'Checking for orders ready to pick…'
          : pickOrderId
            ? 'Aisle-ordered pick list'
            : 'No orders are ready to pick',
      enabled: pickPerm !== 'hidden' && Boolean(pickOrderId),
      go: () => {
        markFloorSession(true);
        if (!pickOrderId) return;
        setStoredOrderId(pickOrderId);
        navigate(pickListPath(pickOrderId));
      },
    },
    {
      id: 'counter',
      title: 'Collection counter',
      blurb: 'Verify collection & handover',
      enabled: counterPerm !== 'hidden',
      go: () => {
        markFloorSession(true);
        navigate('/collection-counter');
      },
    },
  ];

  return (
    <ScreenFrame
      screenId="SCR-MER-062B"
      title="Choose floor mode"
      state="idle"
      showScreenId={false}
      keepContentOnEmpty
    >
      <PermissionGate>
        <div data-theme="floor">
          <p className="mb-field text-muted">PIN accepted, pick your station for this shift.</p>
          <div className="mer-floor-mode-grid" data-testid="merchant-floor-mode-switcher">
            {modes.map((mode) => (
              <button
                key={mode.id}
                type="button"
                className="mer-floor-mode-card m09-target m09-mode-card"
                disabled={!mode.enabled}
                onClick={() => mode.go()}
                data-mode={mode.id}
              >
                <span className="mer-floor-mode-symbol" aria-hidden="true">
                  {mode.id === 'kitchen' ? (
                    <ChefHat size={28} />
                  ) : mode.id === 'picker' ? (
                    <ShoppingBasket size={28} />
                  ) : (
                    <PackageCheck size={28} />
                  )}
                </span>
                <h3>{mode.title}</h3>
                <p>{mode.blurb}</p>
              </button>
            ))}
          </div>
          <Link
            className="mer-floor-skip"
            to={orderBoardPath()}
            onClick={() => markFloorSession(false)}
          >
            Skip to live orders (office)
          </Link>
        </div>
      </PermissionGate>
    </ScreenFrame>
  );
}

export function FloorExitControl() {
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);

  return (
    <div className="mer-floor-exit" data-testid="merchant-floor-exit">
      {!confirming ? (
        <Button
          type="button"
          variant="secondary"
          className="m09-target"
          onClick={() => setConfirming(true)}
        >
          Exit floor
        </Button>
      ) : (
        <div className="mer-floor-exit-confirm">
          <span>Leave floor mode?</span>
          <Button
            type="button"
            className="m09-target"
            onClick={() => {
              markFloorSession(false);
              navigate(orderBoardPath());
            }}
          >
            Confirm exit
          </Button>
          <Button
            type="button"
            variant="secondary"
            className="m09-target"
            onClick={() => setConfirming(false)}
          >
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}
